import { useEffect, useRef } from "react";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// One script load per page, shared by every widget instance.
let scriptPromise = null;

export function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        scriptPromise = null;
        reject(new Error("Turnstile script loaded without exposing window.turnstile"));
      }
    };
    script.onerror = () => {
      // Allow a later mount to retry the load.
      scriptPromise = null;
      script.remove();
      reject(new Error("Failed to load the Turnstile script"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Cloudflare Turnstile challenge in managed mode.
 * onToken receives the token once the visitor passes. onError fires when the script
 * fails to load or the widget reports an error. Changing the key remounts the widget.
 */
export default function TurnstileWidget({ siteKey, action, onToken, onError }) {
  const containerRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onTokenRef.current = onToken;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    let cancelled = false;
    let widgetId = null;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) {
          return;
        }
        widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          callback: (token) => onTokenRef.current?.(token),
          "error-callback": () => onErrorRef.current?.(),
          "expired-callback": () => {
            // A token that expired before use needs a fresh challenge, not an error message.
            window.turnstile?.reset?.(widgetId);
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          onErrorRef.current?.();
        }
      });

    return () => {
      cancelled = true;
      if (widgetId !== null && window.turnstile?.remove) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [siteKey, action]);

  return <div ref={containerRef} />;
}
